import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  Drawer,
  Button,
  Tooltip,
  Stack,
  Chip,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ProSidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../theme";
import { MenuItems, useUserAuth } from "./navConfig";
import LogoDark from "../assets/imgs/logo-dark.svg";
import LogoLight from "../assets/imgs/EduSkill_NEST.png";
import { Icon } from "@iconify/react";
import { useSelector } from "react-redux";
import axios from "axios";
import { BASE_URL } from "../services/configUrls";

const Item = ({
  title,
  to,
  icon,
  selected,
  setSelected,
  isMobileView,
  setToggleDrawer,
  badge, // Added badge prop
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const handleSelection = () => {
    setSelected(title);
    if (isMobileView) {
      setToggleDrawer(false);
    }
  };

  return (
    <MenuItem
      active={selected === title}
      style={{
        color: selected === title ? "#0d47a1" : "#64748b",
        position: "relative",
      }}
      onClick={handleSelection}
      icon={icon}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          minWidth: 0,
        }}
      >
        <Typography sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: "12px", fontWeight: 500 }}>{title}</Typography>
        {badge && (
          <Chip
            label={badge}
            size="small"
            sx={{
              height: "18px",
              fontSize: "10px",
              fontWeight: 600,
              backgroundColor: "#ff9800",
              color: "#fff",
              borderRadius: "4px",
              ml: 1,
              "& .MuiChip-label": {
                px: 0.5,
              },
            }}
          />
        )}
      </Box>
      <Link to={to} />
    </MenuItem>
  );
};

const Sidebar = ({ toggleDrawer, setToggleDrawer }) => {
  const { isLoading } = useUserAuth();
  const [navKey, setNavKey] = useState(0);
  const menuItems = MenuItems();
  const location = useLocation();
  const currentPath = location.pathname;
  const navigate = useNavigate();
  const isActiveRole = useSelector((state) => state.authorise.userRole);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [selected, setSelected] = useState("Dashboard");
  const [isMobileView, setIsMobileView] = useState(
    window.innerWidth < theme.breakpoints.values.lg
  );

  const [showFeedbackTooltip, setShowFeedbackTooltip] = useState(false);

  useEffect(() => {
    const fetchUserHistoryPermissions = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        if (!token) return;
        const res = await axios.get(`${BASE_URL}/event-history-access/my-permissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res?.data) {
          localStorage.setItem("my_event_history_permissions", JSON.stringify(res.data));
          setNavKey((k) => k + 1);
        }
      } catch (err) {
        // Fallback silently if offline
      }
    };

    fetchUserHistoryPermissions();

    const handlePermissionsUpdated = () => {
      fetchUserHistoryPermissions();
      setNavKey((k) => k + 1);
    };
    window.addEventListener("historyAccessPermissionsUpdated", handlePermissionsUpdated);
    window.addEventListener("storage", handlePermissionsUpdated);
    return () => {
      window.removeEventListener("historyAccessPermissionsUpdated", handlePermissionsUpdated);
      window.removeEventListener("storage", handlePermissionsUpdated);
    };
  }, [isActiveRole]);

  useEffect(() => {
    if (currentPath === "/help") {
      setSelected("Help");
      return;
    }

    let selectedTitle = "Dashboard"; // Default fallback

    // Create a flat array of all menu items (parent and children)
    const allMenuItems = [];

    menuItems.forEach((item) => {
      allMenuItems.push(item);
      if (item.children) {
        item.children.forEach((child) => {
          allMenuItems.push(child);
        });
      }
    });

    // Find exact path match, prioritizing child items over parent items
    const childMatch = allMenuItems.find(
      (item) => item.parent && item.path === currentPath
    );

    const parentMatch = allMenuItems.find(
      (item) => !item.parent && item.path === currentPath
    );

    if (childMatch) {
      selectedTitle = childMatch.title;
    } else if (parentMatch) {
      selectedTitle = parentMatch.title;
    }

    setSelected(selectedTitle);
  }, [currentPath, menuItems]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < theme.breakpoints.values.lg);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [theme.breakpoints.values.lg]);

  const handleFeedbackClick = () => {
    localStorage.setItem("hasProvidedFeedback", "true");
    // You can also navigate to the feedback page here
    navigate("/feedback");
  };

  const handleSubscriptionClick = () => {
    navigate("/subscription");
  };

  useEffect(() => {
    // Show the tooltip 5 seconds after the dashboard is opened
    const openTooltipTimeout = setTimeout(() => {
      setShowFeedbackTooltip(true);
    }, 5000);

    // Close the tooltip 10 seconds after opening
    const closeTooltipTimeout = setTimeout(() => {
      setShowFeedbackTooltip(false);
    }, 15000);

    // Cleanup timeouts when the component unmounts
    return () => {
      clearTimeout(openTooltipTimeout);
      clearTimeout(closeTooltipTimeout);
    };
  }, []);

  const feedbackMessages = [
    "Share Your Thoughts",
    "Your Feedback Matters",
    "We Value Your Input",
    // "Help Us Improve",
    "Click to Give Feedback",
  ];

  const randomMessage =
    feedbackMessages[Math.floor(Math.random() * feedbackMessages.length)];

  const feedbackButtonLabel =
    localStorage.getItem("hasProvidedFeedback") === "true"
      ? randomMessage
      : null;

  // Modern white theme colors
  const sidebarBg = "#ffffff";
  const headerBg = "#f8fafc";
  const textPrimary = "#1e293b";
  const textSecondary = "#64748b";
  const activeColor = "#0d47a1"; // Deeper blue
  const hoverBg = "#d1e7fd"; // Deeper blue background for both hover and active
  const activeBg = "#d1e7fd"; // Same as hover for consistency
  const borderColor = "#e2e8f0";

  const SidebarContent = (
    <Box
      sx={{
        "& .pro-sidebar-inner": {
          background: `${sidebarBg} !important`,
          boxShadow:
            "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
          borderRight: `1px solid ${borderColor}`,
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
          color: `${textSecondary} !important`,
        },
        "& .pro-inner-item": {
          p: "4px 12px !important",
          color: `${textSecondary} !important`,
          borderRadius: "8px",
          // margin: "2px 8px",
          fontSize: "12px",
          fontWeight: 500,
        },
        "& .pro-inner-item:hover": {
          color: `${activeColor} !important`,
          backgroundColor: `${hoverBg} !important`,
          borderLeft: `3px solid ${activeColor}`,
          transition: "all 0.2s ease-out",
        },
        "& .pro-menu-item.active": {
          color: `${activeColor} !important`,
          backgroundColor: `${activeBg} !important`,
          borderLeft: `3px solid ${activeColor}`,
          borderRadius: `8px`,
          margin: "2px 8px",
          fontWeight: 600,
        },
        "& .pro-menu-item .pro-icon": {
          color: `${textSecondary} !important`,
          fontSize: "16px",
        },
        "& .pro-menu-item:hover .pro-icon": {
          color: `${activeColor} !important`,
        },
        "& .pro-menu-item.active .pro-icon": {
          color: `${activeColor} !important`,
        },
        "& .pro-item-content": {
          color: `${textSecondary} !important`,
          whiteSpace: "normal !important",
          wordBreak: "break-word !important",
          lineHeight: "1.25 !important",
          fontSize: "12px !important",
          fontWeight: "500 !important",
          overflow: "visible !important",
        },
        "& .pro-sub-menu": {
          backgroundColor: `${sidebarBg} !important`,
        },
        "& .pro-sub-menu .pro-inner-item": {
          // paddingLeft: "40px !important",
          fontSize: "12px",
        },
        "& .pro-arrow": {
          color: `${textSecondary} !important`,
        },
        "& .pro-menu": {
          color: `${textSecondary} !important`,
          paddingTop: "8px",
        },
        "& .pro-sidebar-layout, & .pro-menu": {
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        },
        "& .logo": {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "sticky",
          top: 0,
          zIndex: 100,
          bgcolor: headerBg,
          borderBottom: `1px solid ${borderColor}`,
          height: "56px",
        },
        "& .logo img": {
          height: "36px",
          padding: "0px",
          transition: "all 0.3s ease-out",
          filter: "brightness(0.8)",
        },
        bgcolor: `${sidebarBg} !important`,
        height: "100vh",
      }}
    >
      <Box
        sx={{
          [theme.breakpoints.down("lg")]: {
            display: "block",
          },
          height: "100%",
          background: `${sidebarBg} !important`,
        }}
      >
        <ProSidebar width="200px" style={{ position: "relative" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: headerBg,
              position: "relative",
              height: "56px",
            }}
          >
            <Box className="logo">
              <img src={LogoLight} alt="Logo" />
            </Box>
          </Box>
          <Menu
            iconShape="square"
            style={{
              padding: "8px 0 76px",
              height: "calc(100% - 56px)",
              boxSizing: "border-box",
              overflowY: "auto",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              color: textSecondary,
            }}
          >
            {menuItems?.map((nav, idx) => (
              <div key={idx}>
                {nav?.children ? (
                  <SubMenu
                    title={
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          lineHeight: 1.25,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {nav.title}
                      </Typography>
                    }
                    icon={nav.icon}
                    style={{
                      color: textSecondary,
                      fontWeight: 500,
                    }}
                  >
                    {nav?.children?.map((child, childIdx) => (
                      <Item
                        key={childIdx}
                        title={child.title}
                        to={child.path}
                        icon={child.icon}
                        selected={selected}
                        setSelected={setSelected}
                        setToggleDrawer={setToggleDrawer}
                        isMobileView={isMobileView}
                        badge={child.badge} // Pass badge to child items
                      />
                    ))}
                  </SubMenu>
                ) : (
                  <Item
                    title={nav.title}
                    to={nav.path}
                    icon={nav.icon}
                    selected={selected}
                    setSelected={setSelected}
                    setToggleDrawer={setToggleDrawer}
                    isMobileView={isMobileView}
                    badge={nav.badge} // Pass badge to parent items
                  />
                )}
              </div>
            ))}
          </Menu>
          {(
            // <Box
            //   sx={{
            //     position: "absolute",
            //     bottom: 0,
            //     left: 0,
            //     right: 0,
            //     zIndex: 1,
            //     p: 1.5,
            //     borderTop: `1px solid ${borderColor}`,
            //     backgroundColor: sidebarBg,
            //   }}
            // >
            //   <Stack>
            //     <Button
            //       fullWidth
            //       startIcon={<Icon icon="material-symbols:help-outline-rounded" width={20} />}
            //       onClick={() => {
            //         setSelected("Help");
            //         navigate("/help");
            //         if (isMobileView) setToggleDrawer(false);
            //       }}
            //       sx={{
            //         justifyContent: "flex-start",
            //         px: 1.5,
            //         py: 0.75,
            //         borderRadius: 1,
            //         borderLeft: currentPath === "/help" ? `3px solid ${activeColor}` : "3px solid transparent",
            //         color: currentPath === "/help" ? activeColor : textSecondary,
            //         backgroundColor: currentPath === "/help" ? activeBg : "transparent",
            //         fontSize: 12,
            //         fontWeight: currentPath === "/help" ? 600 : 500,
            //         textTransform: "none",
            //         "&:hover": { backgroundColor: hoverBg, color: activeColor },
            //         "& .MuiButton-startIcon": { color: "inherit" },
            //       }}
            //     >
            //       Help
            //     </Button>
            //     {/* Subscription Button */}
            //     {/* <Button
            //       sx={{
            //         width: "100%",
            //         textTransform: "none",
            //         color: textPrimary,
            //         borderColor: borderColor,
            //         backgroundColor: "transparent",
            //         fontWeight: 500,
            //         borderRadius: 2,
            //         "&:hover": {
            //           backgroundColor: hoverBg,
            //           borderColor: activeColor,
            //           color: activeColor,
            //         },
            //       }}
            //       color={currentPath === "/subscription" ? "primary" : "inherit"}
            //       variant="outlined"
            //       startIcon={<Icon icon="mdi:crown-outline" />}
            //       onClick={handleSubscriptionClick}
            //     >
            //       Subscription
            //     </Button> */}

            //     {/* Feedback Button */}
            //     {/* <Tooltip
            //       title={feedbackButtonLabel}
            //       open={showFeedbackTooltip}
            //       arrow
            //     >
            //       <Button
            //         sx={{
            //           width: "100%",
            //           textTransform: "none",
            //           color: textPrimary,
            //           borderColor: borderColor,
            //           backgroundColor: "transparent",
            //           fontWeight: 500,
            //           borderRadius: 2,
            //           "&:hover": {
            //             backgroundColor: hoverBg,
            //             borderColor: activeColor,
            //             color: activeColor,
            //           },
            //         }}
            //         color={currentPath === "/feedback" ? "primary" : "inherit"}
            //         variant="outlined"
            //         startIcon={
            //           <Icon icon="fluent:person-feedback-16-regular" />
            //         }
            //         onClick={handleFeedbackClick}
            //       >
            //         Feedback
            //       </Button>
            //     </Tooltip> */}
            //   </Stack>
            // </Box>
            <>
            
            </>
          )}
        </ProSidebar>
      </Box>
    </Box>
  );

  if (currentPath === "/email_builder") {
    return null;
  }

  return (
    <Box>
      {isMobileView ? (
        <Drawer
          variant="temporary"
          anchor="left"
          open={toggleDrawer}
          onClose={() => setToggleDrawer(false)}
          PaperProps={{
            sx: {
              boxShadow: "0 25px 50px -12px rgb(0 0 0 / 0.25)",
            },
          }}
        >
          {SidebarContent}
        </Drawer>
      ) : (
        SidebarContent
      )}
    </Box>
  );
};

export default Sidebar;
